/**
 * Copy-index separator so quantity clones share a group id the way cabinets
 * share `job_cabinet_id` (`123-0`, `123-1`) without colliding with legacy
 * `{face}-{instance}` client ids (`5-1`, `fridge-1`).
 */
export const PLACEHOLDER_COPY_SEP = '~';

/**
 * Most copies of one appliance in a single product. Each copy clones the
 * GLB and posts its own save, so the editor and the spawn path share this.
 */
export const MAX_PLACEHOLDER_QUANTITY = 20;

/**
 * Clamp a quantity field into 1..MAX_PLACEHOLDER_QUANTITY.
 * Empty and non-numeric values become 1.
 *
 * @param {number} quantity Raw quantity
 * @return {number} Count the planner will actually create
 */
export const clampPlaceholderQuantity = (quantity: number): number =>
    Math.max(1, Math.min(MAX_PLACEHOLDER_QUANTITY, Number(quantity) || 1));

export type PlaceholderIdentity = {
    groupId: string;
    index: number;
};

type PlaceholderLike = {
    userData?: {
        id?: string | number;
        placeholderGroupId?: string;
        values?: {quantity?: number};
    };
};

const readMeshId = (
    meshOrId: PlaceholderLike | string | number | undefined | null
): string => {
    if (meshOrId && typeof meshOrId === 'object') {
        return String(meshOrId.userData?.id ?? '');
    }
    return String(meshOrId ?? '');
};

/**
 * Parse a placeholder client id into the shared product group and copy index.
 * Legacy ids without `~` are a group of one (index 0).
 *
 * @param {unknown} id Client id (`5-2~1` or legacy `5-2`)
 * @return {PlaceholderIdentity} Group id and 0-based copy index
 */
export const parsePlaceholderIdentity = (id: unknown): PlaceholderIdentity => {
    const value = String(id ?? '');
    if (!value) {
        return {groupId: '', index: 0};
    }

    const separatorIndex = value.lastIndexOf(PLACEHOLDER_COPY_SEP);
    if (separatorIndex < 0) {
        return {groupId: value, index: 0};
    }

    const index = Number(value.slice(separatorIndex + 1));
    return {
        groupId: value.slice(0, separatorIndex),
        index: Number.isNaN(index) ? 0 : index,
    };
};

/**
 * Client id for one visual copy of a placeholder product.
 *
 * @param {string} groupId Shared product group (`5-2`)
 * @param {number} index 0-based copy index
 * @return {string} e.g. `5-2~0`
 */
export const makePlaceholderCopyId = (groupId: string, index: number): string =>
    `${groupId}${PLACEHOLDER_COPY_SEP}${index}`;

/**
 * Shared product id for a placeholder mesh (one overall item).
 *
 * @param {PlaceholderLike | string | number | undefined | null} meshOrId Mesh or client id
 * @return {string} Group id
 */
export const getPlaceholderGroupId = (
    meshOrId: PlaceholderLike | string | number | undefined | null
): string => {
    if (meshOrId && typeof meshOrId === 'object') {
        const stored = meshOrId.userData?.placeholderGroupId;
        if (stored) {
            return String(stored);
        }
    }
    return parsePlaceholderIdentity(readMeshId(meshOrId)).groupId;
};

/**
 * Copy index for layout persistence / duplicate_id.
 *
 * @param {PlaceholderLike | string | number | undefined | null} meshOrId Mesh or client id
 * @return {number} 0-based index
 */
export const getPlaceholderCopyIndex = (
    meshOrId: PlaceholderLike | string | number | undefined | null
): number => parsePlaceholderIdentity(readMeshId(meshOrId)).index;

/**
 * Scene id of a layout placement.
 *
 * A saved quantity group comes back as `group_id` plus `index`
 * (`5-2` and `1` → `5-2~1`). A placement made in this session keeps the
 * `scene_id` it was spawned with until the layouts API replaces it. A row
 * stored before grouping was persisted is a group of one keyed by its
 * catalogue entry and row id (`5-12`).
 *
 * @param {object} placement Layout placement
 * @return {string} Scene id
 */
export const placeholderSceneId = (placement: {
    id?: number;
    placeholder_id: number;
    scene_id?: string;
    group_id?: string | null;
    index?: number | null;
}): string => {
    if (placement.scene_id) {
        return placement.scene_id;
    }
    if (placement.group_id) {
        const index = Number(placement.index);
        return makePlaceholderCopyId(
            placement.group_id,
            Number.isFinite(index) ? index : 0
        );
    }
    return `${placement.placeholder_id}-${placement.id}`;
};

/**
 * Next unused group id for a catalog base (`1`, `fridge`, ...).
 * Looks at group ids so `5-2~0` / `5-2~1` count as one product.
 *
 * @param {string} base Face / catalog prefix
 * @param {string[]} existingIds Live client ids
 * @return {string} e.g. `5-3`
 */
export const nextPlaceholderGroupId = (
    base: string,
    existingIds: string[]
): string => {
    let max = 0;
    const prefix = `${base}-`;
    existingIds.forEach((id) => {
        const groupId = getPlaceholderGroupId(id);
        if (!groupId.startsWith(prefix)) {
            return;
        }
        const rest = groupId.slice(prefix.length);
        const suffix = Number(rest);
        if (rest !== '' && !rest.includes('-') && !Number.isNaN(suffix)) {
            max = Math.max(max, suffix);
        }
    });
    return `${base}-${max + 1}`;
};

/**
 * All visual copies of the same placeholder product.
 *
 * @param {PlaceholderLike[]} list Live placeholder meshes
 * @param {string} groupId Shared group id
 * @return {PlaceholderLike[]} Members of that product
 */
export const findPlaceholderGroup = <T extends PlaceholderLike>(
    list: T[] | undefined | null,
    groupId: string
): T[] => {
    if (!groupId || !list) {
        return [];
    }
    return list.filter((item) => getPlaceholderGroupId(item) === groupId);
};

/**
 * Visual copies that belong with `mesh`.
 *
 * @param {PlaceholderLike[]} list Live placeholder meshes
 * @param {PlaceholderLike | undefined | null} mesh Selected copy
 * @return {PlaceholderLike[]} Group members (includes `mesh` when present)
 */
export const findPlaceholderGroupOf = <T extends PlaceholderLike>(
    list: T[] | undefined | null,
    mesh: T | undefined | null
): T[] => {
    if (!mesh) {
        return [];
    }
    return findPlaceholderGroup(list, getPlaceholderGroupId(mesh));
};

/**
 * Stored quantity, falling back to 1.
 *
 * @param {PlaceholderLike | undefined | null} mesh Placeholder mesh
 * @return {number} Quantity >= 1
 */
export const getPlaceholderQuantity = (
    mesh: PlaceholderLike | undefined | null
): number => Math.max(1, Number(mesh?.userData?.values?.quantity) || 1);

/**
 * Write the same quantity onto every copy (cabinet `cabinet_quantity`).
 *
 * @param {PlaceholderLike[]} members Group members
 * @param {number} quantity Product quantity
 * @return {void}
 */
export const setPlaceholderGroupQuantityValue = (
    members: PlaceholderLike[],
    quantity: number
): void => {
    const next = Math.max(1, quantity);
    members.forEach((member) => {
        const values = {
            ...(member.userData?.values || {}),
            quantity: next,
        };
        if (!member.userData) {
            member.userData = {values};
            return;
        }
        member.userData.values = values;
        member.userData.placeholderGroupId = getPlaceholderGroupId(member);
    });
};

/**
 * After restore / delete, quantity is the number of live copies.
 *
 * @param {PlaceholderLike[]} list Live placeholder meshes
 * @return {void}
 */
export const syncPlaceholderQuantitiesFromGroups = (
    list: PlaceholderLike[] | undefined | null
): void => {
    if (!list) {
        return;
    }
    const byGroup = new Map<string, PlaceholderLike[]>();
    list.forEach((item) => {
        const groupId = getPlaceholderGroupId(item);
        if (!groupId) {
            return;
        }
        const members = byGroup.get(groupId) ?? [];
        members.push(item);
        byGroup.set(groupId, members);
    });
    byGroup.forEach((members) => {
        setPlaceholderGroupQuantityValue(members, members.length);
    });
};

/**
 * Highest copy index currently used in a group.
 *
 * @param {PlaceholderLike[]} members Group members
 * @return {number} Max index, or -1 when empty
 */
export const getMaxPlaceholderCopyIndex = (
    members: PlaceholderLike[]
): number => {
    if (members.length === 0) {
        return -1;
    }
    return Math.max(
        ...members.map((member) => getPlaceholderCopyIndex(member))
    );
};
